import React, { useState, useEffect } from "react";
import {
	Container,
	Box,
	CircularProgress,
	Typography,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaDownload, FaCalendar } from "react-icons/fa";
import axios from "axios";

const LogsPage = () => {
	const [files, setFiles] = useState([]);
	const [months, setMonths] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState("");
	const [zipping, setZipping] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const token = localStorage.getItem("parallextoken");
				if (!token) {
					setFiles([]);
					setMonths([]);
					return;
				}
				const res = await axios.get("https://parallexsms.approot.ng/list.php", {
					headers: { Authorization: `Bearer ${token}` },
				});
				if (res.data.status === "success") {
					// Set individual files
					const rows = res.data.files.map((file, index) => ({
						id: index + 1,
						name: file,
					}));
					setFiles(rows);

					// Set available months
					setMonths(res.data.months || []);
				}
			} catch (err) {
				console.error("Failed to fetch data:", err);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	const handleDownload = async (filename) => {
		const token = localStorage.getItem("parallextoken");
		if (!token) {
			alert("Your session has expired. Please log in again.");
			return;
		}

		try {
			const response = await axios.get(
				`https://parallexsms.approot.ng/download.php?file=${encodeURIComponent(filename)}`,
				{ headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
			);
			const url = window.URL.createObjectURL(response.data);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Log download failed:", err);
			alert("Download failed. Please verify your session and try again.");
		}
	};

	const handleMonthDownload = async () => {
		if (!selectedMonth) return alert("Please select a month first.");
		setZipping(true);
		try {
			const token = localStorage.getItem("parallextoken");
			if (!token) {
				throw new Error("Your session has expired. Please log in again.");
			}
			const response = await axios.get(
				`https://parallexsms.approot.ng/download_month.php?month=${encodeURIComponent(selectedMonth)}`,
				{ headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
			);
			const blob = response.data;
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `parallex_report_${selectedMonth}.zip`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			alert("Download failed: " + err.message);
		} finally {
			setZipping(false);
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
	};

	const columns = [
		{ field: "name", headerName: "Filename", flex: 1, minWidth: 250 },
		{
			field: "actions",
			headerName: "Download",
			width: 130,
			renderCell: (params) => (
				<Button
					variant='contained'
					size='small'
					sx={{
						backgroundColor: "#DB9E27",
						"&:hover": { backgroundColor: "#c7981f" },
						color: "#fff",
						fontWeight: "bold",
					}}
					onClick={() => handleDownload(params.row.name)}
				>
					<FaDownload style={{ marginRight: 6 }} />
					Download
				</Button>
			),
		},
	];

	return (
		<Container maxWidth='lg' sx={{ mt: 4, pb: 4 }}>
			<Typography
				variant='h4'
				gutterBottom
				sx={{ fontWeight: "bold", color: "#221891" }}
			>
				Logs Archive
			</Typography>

			{/* Monthly Reports Section */}
			{months.length > 0 && (
				<Box
					sx={{
						mb: 4,
						p: 3,
						backgroundColor: "#f5f5f5",
						borderRadius: 2,
						border: "1px solid #e0e0e0",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
						<FaCalendar style={{ marginRight: 8, color: "#221891" }} />
						<Typography variant='h6' sx={{ fontWeight: "bold" }}>
							Monthly Reports
						</Typography>
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
						<FormControl sx={{ minWidth: 200 }}>
							<InputLabel>Select Month</InputLabel>
							<Select
								value={selectedMonth}
								onChange={(e) => setSelectedMonth(e.target.value)}
								label='Select Month'
								sx={{ backgroundColor: "#fff" }}
							>
								{months.map((month) => (
									<MenuItem key={month.value} value={month.value}>
										{month.display}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Button
							variant='contained'
							onClick={handleMonthDownload}
							disabled={zipping || !selectedMonth}
							sx={{
								backgroundColor: "#221891",
								"&:hover": { backgroundColor: "#1a1270" },
								color: "#fff",
								fontWeight: "bold",
								height: 56,
								px: 3,
							}}
						>
							{zipping ? (
								<CircularProgress size={20} color='inherit' />
							) : (
								<>
									<FaDownload style={{ marginRight: 8 }} /> Download ZIP
								</>
							)}
						</Button>
					</Box>

					{/* Available months chips */}
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						<Typography variant='body2' sx={{ mr: 1, alignSelf: "center" }}>
							Available:
						</Typography>
						{months.map((month) => (
							<Chip
								key={month.value}
								label={`${month.display} (${formatFileSize(month.size)})`}
								size='small'
								sx={{
									backgroundColor: "#e3f2fd",
									color: "#221891",
									fontWeight: 500,
								}}
							/>
						))}
					</Box>
				</Box>
			)}

			{/* Individual Files Section */}
			{files.length > 0 && (
				<>
					<Typography
						variant='h6'
						gutterBottom
						sx={{ fontWeight: "bold", color: "#221891", mb: 2 }}
					>
						Individual Files
					</Typography>

					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
							<CircularProgress />
						</Box>
					) : (
						<Box sx={{ height: 600, width: "100%" }}>
							<DataGrid
								rows={files}
								columns={columns}
								initialState={{
									pagination: { paginationModel: { pageSize: 10, page: 0 } },
								}}
								pageSizeOptions={[10, 25, 50, 100]}
								disableRowSelectionOnClick
								sx={{
									borderRadius: 2,
									boxShadow: 1,
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#221891",
										color: "#fff",
									},
									"& .MuiDataGrid-row:hover": { backgroundColor: "#f9f9f9" },
								}}
							/>
						</Box>
					)}
				</>
			)}

			{/* Loading State */}
			{loading && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
					<CircularProgress />
				</Box>
			)}

			{/* No Data State */}
			{!loading && months.length === 0 && files.length === 0 && (
				<Box sx={{ textAlign: "center", mt: 6, p: 4 }}>
					<Typography variant='h6' color='text.secondary'>
						No reports available yet
					</Typography>
				</Box>
			)}
		</Container>
	);
};

export default LogsPage;
